import React, { useState, useEffect } from 'react';
import {
  fetchMyFees,
  type StudentFeeAccountDto,
  type FeePaymentDto,
  fetchStudentTermProfile,
  fetchSchoolSettings,
} from '../api/axios';
import { useSchool } from '../context/SchoolContext';
import styles from './StudentFees.module.css';

export const StudentFees: React.FC = () => {
  const { currentSchool } = useSchool();
  const [accounts, setAccounts] = useState<StudentFeeAccountDto[]>([]);
  const [payments, setPayments] = useState<FeePaymentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [blockingMessage, setBlockingMessage] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      setBlockingMessage(null);
      setAccounts([]);
      setPayments([]);
      let academicYear = new Date().getFullYear().toString();
      let term = 1;
      try {
        const settings = await fetchSchoolSettings(currentSchool);
        academicYear = settings.current_academic_year;
        term = settings.current_term;
      } catch { /* fall back to defaults */ }
      try {
        const res = await fetchStudentTermProfile({ academic_year: academicYear, term });
        if (!res.profile || !res.profile.boarding_status) {
          setBlockingMessage(
            'Before you can view your fees, you must set your Student Type as either Day Scholar or Boarder in the Profile section. This is very important for accurate fee calculation.'
          );
          setLoading(false);
          return;
        }
      } catch {
        setBlockingMessage(
          'Before you can view your fees, you must set your Student Type as either Day Scholar or Boarder in the Profile section. This is very important for accurate fee calculation.'
        );
        setLoading(false);
        return;
      }

      await loadFees();
    };

    void init();
  }, [currentSchool]);

  const loadFees = async () => {
    setLoading(true);
    try {
      const data = await fetchMyFees();
      setAccounts(data.accounts);
      setPayments(data.payments);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const formatMK = (val: string | number) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    return `MK${num.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  if (loading) {
    return <div className={styles.loading}>Loading fee information...</div>;
  }

  if (blockingMessage) {
    return (
      <div className={styles.container}>
        <div className={styles.titleRow}>
          <h2 className={styles.title}>School Fees</h2>
          <div className={styles.learnMoreTrigger}>
            <button type="button" aria-label="Learn more about School Fees" className={styles.learnMoreButton}>?</button>
            <div className={styles.learnMoreTooltip}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>How to use this page</div>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                <li>Set your student type in Profile first.</li>
                <li>Return here to view balances and payment history.</li>
                <li>Contact admin if fee records look incorrect.</li>
              </ul>
            </div>
          </div>
        </div>
        <div className={styles.blockingCard}>
          <p>{blockingMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.titleRow}>
        <h2 className={styles.title}>School Fees</h2>
        <div className={styles.learnMoreTrigger}>
          <button type="button" aria-label="Learn more about School Fees" className={styles.learnMoreButton}>?</button>
          <div className={styles.learnMoreTooltip}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>How to use this page</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              <li>Review each term card for total fee, paid amount, and balance.</li>
              <li>Check Payment History for receipts and payment dates.</li>
              <li>Raise mismatches with admin using your receipt number.</li>
            </ul>
          </div>
        </div>
      </div>
      <p className={styles.subtitle}>
        View your fee balances and payment history for each term.
      </p>

      {accounts.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No fee records found.</p>
          <p>Fee accounts are set up by the school administration.</p>
        </div>
      ) : (
        <>
          <div className={styles.accountsGrid}>
            {accounts.map((acc) => {
              const balance = parseFloat(acc.balance);
              const totalFee = parseFloat(acc.total_fee);
              const amountPaid = parseFloat(acc.amount_paid);
              const carryOver = parseFloat(acc.carry_over_credit || '0');
              const isPaid = totalFee > 0 && balance <= 0;
              const hasCredit = balance < 0;

              return (
                <div key={acc.id} className={styles.accountCard}>
                  <div className={styles.accountHeader}>
                    <span className={styles.accountTerm}>Term {acc.term}</span>
                    <span className={styles.accountYear}>{acc.academic_year}</span>
                  </div>

                  <div className={styles.accountRow}>
                    <span className={styles.accountLabel}>Total Fee</span>
                    <span className={styles.accountAmount}>{formatMK(acc.total_fee)}</span>
                  </div>
                  {parseFloat(acc.opening_balance || '0') > 0 && (
                    <div className={styles.accountRow}>
                      <span className={styles.accountLabel}>Brought forward</span>
                      <span className={styles.accountAmount}>{formatMK(acc.opening_balance || '0')}</span>
                    </div>
                  )}
                  {carryOver > 0 && (
                    <div className={styles.accountRow}>
                      <span className={styles.accountLabel}>Credit from Previous Term</span>
                      <span className={styles.accountAmount} style={{ color: '#2563eb', fontWeight: 600 }}>
                        {formatMK(carryOver)}
                      </span>
                    </div>
                  )}
                  <div className={styles.accountRow}>
                    <span className={styles.accountLabel}>Amount Paid</span>
                    <span className={styles.accountAmount} style={{ color: amountPaid > 0 ? '#059669' : '#6b7280' }}>
                      {formatMK(acc.amount_paid)}
                    </span>
                  </div>
                  <div className={styles.accountRow} style={{ paddingTop: 12 }}>
                    <span className={styles.accountLabel}>
                      {hasCredit ? 'Credit for Next Term' : 'Balance Remaining'}
                    </span>
                    <span className={isPaid ? styles.balancePaid : styles.balancePositive}>
                      {hasCredit
                        ? `${formatMK(Math.abs(balance))} credit`
                        : isPaid
                          ? 'FULLY PAID'
                          : formatMK(acc.balance)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={styles.paymentsSection}>
            <h3 className={styles.sectionTitle}>Payment History</h3>
            {payments.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No payments recorded yet.</p>
              </div>
            ) : (
              <div className="tableScroll">
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Receipt #</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Term</th>
                      <th>Year</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id}>
                        <td>
                          <span className={styles.receiptBadge}>{p.receipt_number}</span>
                        </td>
                        <td style={{ fontWeight: 600 }}>{formatMK(p.amount)}</td>
                        <td>{new Date(p.payment_date).toLocaleDateString('en-GB')}</td>
                        <td>Term {p.term}</td>
                        <td>{p.academic_year}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
